import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarCheck, MapPin, Video, Check, Clock } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { fadeUp } from "@/lib/motion";

const API = process.env.REACT_APP_BACKEND_URL;

const getBookableDays = () => {
  const days = [];
  const d = new Date();
  for (let i = 1; i <= 28; i++) {
    const day = new Date(d);
    day.setDate(d.getDate() + i);
    const dow = day.getDay();
    if (dow >= 1 && dow <= 5) days.push(day);
  }
  return days;
};

const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const weekdayFmt = new Intl.DateTimeFormat("el-GR", { weekday: "short" });
const dayFmt = new Intl.DateTimeFormat("el-GR", { day: "numeric" });
const monthFmt = new Intl.DateTimeFormat("el-GR", { month: "short" });
const fullFmt = new Intl.DateTimeFormat("el-GR", {
  weekday: "long",
  day: "numeric",
  month: "long",
});

export default function Rantevou() {
  const days = useMemo(getBookableDays, []);
  const [type, setType] = useState("in_person");
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!selectedDate) return;
    setSlots(null);
    setSelectedTime(null);
    fetch(`${API}/api/bookings/availability?date=${selectedDate}`)
      .then((r) => r.json())
      .then((d) => setSlots(d.slots || []))
      .catch(() => setSlots([]));
  }, [selectedDate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      setError("Διάλεξε ημέρα και ώρα.");
      return;
    }
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError("Συμπλήρωσε όνομα, email και τηλέφωνο.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          type,
          date: selectedDate,
          time: selectedTime,
          note: form.note.trim() || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setError("Η ώρα αυτή μόλις δεσμεύτηκε — διάλεξε άλλη.");
        setSelectedTime(null);
        const av = await fetch(`${API}/api/bookings/availability?date=${selectedDate}`).then((r) => r.json());
        setSlots(av.slots || []);
      } else if (!res.ok) {
        setError(data.detail || "Κάτι πήγε στραβά. Δοκίμασε ξανά.");
      } else {
        setDone({ date: selectedDate, time: selectedTime });
      }
    } catch {
      setError("Κάτι πήγε στραβά. Δοκίμασε ξανά.");
    }
    setSending(false);
  };

  const inputCls =
    "w-full rounded-xl bg-card border border-border px-4 py-3 font-body text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60";

  return (
    <>
      <Helmet>
        <html lang="el-GR" />
        <title>{"Κλείσε Ραντεβού Online · Ψυχοθεραπεία Πάτρα · Άννα Παπαχρονόπουλου"}</title>
        <meta
          name="description"
          content="Κλείσε online ραντεβού με την ψυχολόγο Άννα Παπαχρονόπουλου στην Πάτρα. Διάλεξε ημέρα και ώρα — δια ζώσης ή online συνεδρία. Επιβεβαίωση με email."
        />
        <link rel="canonical" href="https://psyloveyourself.com/rantevou" />
      </Helmet>

      <Header />

      <main className="bg-background pt-32 pb-24 min-h-screen" data-testid="booking-page">
        <div className="max-w-[880px] mx-auto px-6 sm:px-12">
          <motion.div {...fadeUp}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground transition-colors mb-10"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              Αρχική
            </Link>

            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-4">
              Online κράτηση
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground">
              Κλείσε <span className="italic font-light">ραντεβού</span>.
            </h1>
            <p className="mt-6 font-body font-light text-muted-foreground leading-relaxed max-w-[560px]">
              Διάλεξε την ημέρα και την ώρα που σε βολεύει. Το αίτημά σου θα
              σταλεί στην Άννα και θα λάβεις email επιβεβαίωσης μόλις το
              εγκρίνει.
            </p>
          </motion.div>

          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="booking-success"
              className="mt-14 p-10 sm:p-14 bg-card border border-border/60 rounded-3xl text-center"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 text-primary mb-6">
                <Check size={24} strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-lg md:text-lg text-foreground">
                Το αίτημά σου στάλθηκε!
              </h2>
              <p className="mt-4 font-body font-light text-sm text-foreground/75 leading-relaxed max-w-[420px] mx-auto">
                Ζήτησες ραντεβού για{" "}
                <strong className="font-medium">
                  {fullFmt.format(new Date(done.date))} στις {done.time}
                </strong>
                . Θα λάβεις email επιβεβαίωσης μόλις η Άννα εγκρίνει το ραντεβού
                — συνήθως μέσα σε λίγες ώρες.
              </p>
              <Link
                to="/"
                data-testid="booking-back-home"
                className="mt-8 inline-flex items-center rounded-full bg-foreground text-background px-7 py-3.5 font-body text-sm font-medium"
              >
                Επιστροφή στην αρχική
              </Link>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mt-14 space-y-12">
              {/* Step 1 — type */}
              <motion.section {...fadeUp}>
                <p className="font-body text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">
                  1 · Τύπος συνεδρίας
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  {[
                    { id: "in_person", label: "Δια ζώσης", sub: "Ρήγα Φεραίου 119, Πάτρα", Icon: MapPin },
                    { id: "online", label: "Online", sub: "Βιντεοκλήση από όπου κι αν είσαι", Icon: Video },
                  ].map(({ id, label, sub, Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setType(id)}
                      data-testid={`booking-type-${id}`}
                      className={`flex-1 flex items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-all ${
                        type === id
                          ? "border-foreground bg-foreground text-background"
                          : "border-border bg-card text-foreground hover:border-foreground/40"
                      }`}
                    >
                      <Icon size={18} strokeWidth={1.5} className="shrink-0" />
                      <span>
                        <span className="block font-body text-sm font-medium">{label}</span>
                        <span className={`block font-body text-xs ${type === id ? "text-background/70" : "text-muted-foreground"}`}>
                          {sub}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </motion.section>

              {/* Step 2 — date */}
              <motion.section {...fadeUp}>
                <p className="font-body text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">
                  2 · Ημέρα <span className="normal-case">(Δευτέρα–Παρασκευή)</span>
                </p>
                <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                  {days.map((d) => {
                    const v = iso(d);
                    const active = selectedDate === v;
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setSelectedDate(v)}
                        data-testid="booking-date"
                        data-date={v}
                        className={`rounded-2xl border py-3 flex flex-col items-center gap-0.5 transition-all ${
                          active
                            ? "border-foreground bg-foreground text-background"
                            : "border-border bg-card text-foreground hover:border-foreground/40"
                        }`}
                      >
                        <span className={`font-body text-[10px] uppercase ${active ? "text-background/70" : "text-muted-foreground"}`}>
                          {weekdayFmt.format(d)}
                        </span>
                        <span className="font-heading text-lg leading-none">{dayFmt.format(d)}</span>
                        <span className={`font-body text-[10px] ${active ? "text-background/70" : "text-muted-foreground"}`}>
                          {monthFmt.format(d)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.section>

              {/* Step 3 — time */}
              {selectedDate && (
                <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                  <p className="font-body text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">
                    3 · Ώρα για {fullFmt.format(new Date(selectedDate))}
                  </p>
                  {slots === null ? (
                    <p className="font-body text-sm text-muted-foreground flex items-center gap-2">
                      <Clock size={14} strokeWidth={1.5} className="animate-pulse" />
                      Φόρτωση διαθέσιμων ωρών…
                    </p>
                  ) : slots.length === 0 ? (
                    <p data-testid="booking-no-slots" className="font-body text-sm text-muted-foreground">
                      Δεν υπάρχουν διαθέσιμες ώρες για αυτή την ημέρα — διάλεξε άλλη.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {slots.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setSelectedTime(t)}
                          data-testid="booking-slot"
                          data-time={t}
                          className={`rounded-full border px-5 py-2.5 font-body text-sm transition-all ${
                            selectedTime === t
                              ? "border-foreground bg-foreground text-background"
                              : "border-border bg-card text-foreground hover:border-foreground/40"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </motion.section>
              )}

              {/* Step 4 — details */}
              {selectedTime && (
                <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                  <p className="font-body text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">
                    4 · Τα στοιχεία σου
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Ονοματεπώνυμο *"
                      data-testid="booking-name"
                      className={inputCls}
                    />
                    <input
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="Email *"
                      type="email"
                      data-testid="booking-email"
                      className={inputCls}
                    />
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Τηλέφωνο *"
                      type="tel"
                      data-testid="booking-phone"
                      className={inputCls}
                    />
                    <input
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      placeholder="Σημείωση (προαιρετικό)"
                      data-testid="booking-note"
                      className={inputCls}
                    />
                  </div>
                  {error && (
                    <p data-testid="booking-error" className="mt-4 font-body text-sm text-destructive">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="booking-submit"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground text-background px-8 py-4 font-body text-sm font-medium disabled:opacity-50 hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <CalendarCheck size={16} strokeWidth={1.5} />
                    {sending ? "Αποστολή…" : "Αποστολή αιτήματος ραντεβού"}
                  </button>
                  <p className="mt-4 font-body text-xs text-muted-foreground leading-relaxed max-w-[480px]">
                    Το ραντεβού οριστικοποιείται μόνο μετά την έγκριση της Άννας
                    — θα λάβεις email επιβεβαίωσης. Δες την{" "}
                    <Link to="/politiki-aporritou" className="underline underline-offset-2">
                      Πολιτική Απορρήτου
                    </Link>
                    .
                  </p>
                </motion.section>
              )}
              {error && !selectedTime && (
                <p data-testid="booking-error-general" className="font-body text-sm text-destructive">{error}</p>
              )}
            </form>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}
